import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Grid, Typography, Checkbox, FormControlLabel, RadioGroup, Radio, MenuItem, Button, Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import dayjs from 'dayjs';
import { ncdAPI } from '../../../../services/ncd.api';


const SignsSymptomsForm = ({ setIsEditing: setParentIsEditing, individualId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { setFieldValue } = useFormikContext();

  const fetchSymptoms = async () => {
    try {
      const response = await ncdAPI.getInitialAssessmentSymptoms(individualId);
      if (response?.success && response?.data) {
        const data = response.data;
        setFieldValue('symptomsDate', data.assesDate || new Date().toISOString().split('T')[0]);
        
        // Set general symptoms
        if (data.general) {
          Object.keys(data.general).forEach(key => {
            setFieldValue(`symptoms.general.${key}`, data.general[key] ?? null);
          });
        }
        
        // Set oral symptoms
        if (data.oral) {
          // Check if all boolean fields are null (excluding 'other')
          const oralBooleanKeys = ['ulcer', 'cavityPatch', 'diffOpngMouth', 'spicyFood', 'voiceChange'];
          const allFieldsNull = oralBooleanKeys.every(k => data.oral[k] === null);
          
          setFieldValue('symptoms.oralNone', allFieldsNull);
          
          // Set each field value
          oralBooleanKeys.forEach(key => {
            if (data.oral[key] === null) {
              setFieldValue(`symptoms.oral.${key}`, null);
            } else if (data.oral[key] !== undefined) {
              setFieldValue(`symptoms.oral.${key}`, data.oral[key]);
            } else {
              // Field is missing from response, keep it unselected
              setFieldValue(`symptoms.oral.${key}`, null);
            }
          });
          
          // Set 'other' field
          setFieldValue('symptoms.oral.other', data.oral.other || '');
        }
        
        // Set breast symptoms
        if (data.breast) {
          const breastBooleanKeys = ['lump', 'dischrg', 'retrctn'];
          const allFieldsNull = breastBooleanKeys.every(k => data.breast[k] === null);
          
          setFieldValue('symptoms.breastNone', allFieldsNull);
          
          breastBooleanKeys.forEach(key => {
            if (data.breast[key] === null) {
              setFieldValue(`symptoms.breast.${key}`, null);
            } else if (data.breast[key] !== undefined) {
              setFieldValue(`symptoms.breast.${key}`, data.breast[key]);
            } else {
              setFieldValue(`symptoms.breast.${key}`, null);
            }
          });
          
          setFieldValue('symptoms.breast.othr', data.breast.othr || '');
        }
        
        // Set cervical symptoms
        if (data.cervical) {
          const cervicalBooleanKeys = ['bldDrngPrd', 'bldBtwnPrd', 'postMeno', 'coitalBld', 'foulSmell'];
          const allFieldsNull = cervicalBooleanKeys.every(k => data.cervical[k] === null);
          
          setFieldValue('symptoms.cervicalNone', allFieldsNull);
          
          cervicalBooleanKeys.forEach(key => {
            if (data.cervical[key] === null) {
              setFieldValue(`symptoms.cervical.${key}`, null);
            } else if (data.cervical[key] !== undefined) {
              setFieldValue(`symptoms.cervical.${key}`, data.cervical[key]);
            } else {
              setFieldValue(`symptoms.cervical.${key}`, null);
            }
          });
          
          setFieldValue('symptoms.cervical.othr', data.cervical.othr || '');
        }
      }
    } catch (error) {
      console.error('Error fetching symptoms:', error);
    }
  };

  useEffect(() => {
    if (individualId) {
      fetchSymptoms();
    }
    window.symptomsFormRef = { fetchSymptoms };
    return () => {
      delete window.symptomsFormRef;
    };
  }, [individualId]);

  const handleEditToggle = () => {
    const newEditingState = !isEditing;
    setIsEditing(newEditingState);
    if (setParentIsEditing) {
      setParentIsEditing(newEditingState);
    }
  };

  const conditions = [
    { key: "excsThirst", label: "Excess Thirst (Polydipsia)" },
    { key: "excsUrinatn", label: "Excess Urination (Polyuria)" },
    { key: "excsHunger", label: "Excess Hunger" },
    { key: "recrntSkin", label: "Recurrent skin/Genito-urinary infection" },
    { key: "woundHeal", label: "Delayed healing of wounds" },
    { key: "dietChng", label: "Change in dietary habits" },
    { key: "visualDistrbnc", label: "Sudden Visual Disturbances history or present" },
    { key: "edema", label: "Significant Edema" },
    { key: "breathlsns", label: "Breathlessness" },
    { key: "angina", label: "Angina (Chest Pain)" },
    { key: "claudicatn", label: "Intermittent Claudication" },
    { key: "limpness", label: "Limpness or lack of strength on one side of limbs" },
  ];

  const oralCancerSymptoms = [
    { key: "ulcer", label: "Ulceration/roughened areas in the oral cavity, especially those that are there for more than three weeks at the site" },
    { key: "cavityPatch", label: "White or Red Patch in the oral cavity" },
    { key: "spicyFood", label: "Difficulty in tolerating spicy foods" },
    { key: "voiceChange", label: "Sudden change in voice/hoarseness" },
    { key: "diffOpngMouth", label: "Difficulty in Opening Mouth" },
  ];

  const breastCancerSymptoms = [
    { key: "lump", label: "Lump or thickening in the breast" },
    { key: "dischrg", label: "Discharge from one or both nipples" },
    { key: "retrctn", label: "Any Retraction of the Nipple" },
  ];

  const cervicalCancerSymptoms = [
    { key: "bldDrngPrd", label: "Excessive bleeding during periods" },
    { key: "bldBtwnPrd", label: "Bleeding between periods" },
    { key: "postMeno", label: "Postmenopausal Bleeding" },
    { key: "coitalBld", label: "Post coital bleeding" },
    { key: "foulSmell", label: "Excessive/foul smelling vaginal Discharge" },
  ];

  return (
    <Box className="form-section">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" className="section-title">Symptoms</Typography>
        <Button
          variant="contained"
          color={isEditing ? "error" : "primary"}
          onClick={handleEditToggle}
          sx={{ textTransform: 'capitalize' }}
        >
          <EditOutlinedIcon /> {isEditing ? 'Cancel' : 'Edit'}
        </Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Field name="symptomsDate">
              {({ field, form }) => (
                <DatePicker
                  label="Date"
                  format="DD/MM/YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(newValue) => {
                    form.setFieldValue('symptomsDate', newValue ? newValue.format('YYYY-MM-DD') : '');
                  }}
                  disabled={!isEditing}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'normal',
                      sx: {
                        opacity: 1,
                        pointerEvents: isEditing ? 'auto' : 'none'
                      }
                    }
                  }}
                />
              )}
            </Field>
          </LocalizationProvider>
        </Grid>
      </Grid>


      <Box className="subsection1">
        <TableContainer>
          <Table size="small">

            <TableBody>
              {conditions.map((condition) => (
                <TableRow key={condition.key}>
                  <TableCell
                    sx={{
                      borderRight: "none !important",
                      py: 1,
                      pl: 0,
                      opacity: 1
                    }}
                  > {condition.label}

                  </TableCell>

                  <TableCell sx={{ py: 1, textAlign: 'right' }}>
                    <Field name={`symptoms.general.${condition.key}`}>
                      {({ field, form }) => (
                        <RadioGroup
                          row
                          sx={{ display: 'inline-flex' }}
                          value={field.value === true ? "Yes" : field.value === false ? "No" : ""}
                          onChange={(e) =>
                            form.setFieldValue(
                              `symptoms.general.${condition.key}`,
                              e.target.value === "Yes" ? true : e.target.value === "No" ? false : null
                            )
                          }
                        >
                          <FormControlLabel
                            value="Yes"
                            control={<Radio sx={{ '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }} />}
                            label="Yes"
                            disabled={!isEditing}
                            sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
                          />
                          <FormControlLabel
                            value="No"
                            control={<Radio sx={{ '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }} />}
                            label="No"
                            disabled={!isEditing}
                            sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
                          />
                        </RadioGroup>
                      )}
                    </Field>
                  </TableCell>

                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Oral Cancer Related Symptoms Section */}
      <Box className="subsection1" sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" className="section-title">Oral cancer related symptoms</Typography>
          <Field name="symptoms.oralNone">
            {({ field, form }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value || false}
                    onChange={(e) => {
                      form.setFieldValue('symptoms.oralNone', e.target.checked);
                      if (e.target.checked) {
                        oralCancerSymptoms.forEach((symptom) => {
                          form.setFieldValue(`symptoms.oral.${symptom.key}`, null);
                        });
                      }
                    }}
                    disabled={!isEditing}
                    sx={{ '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
                  />
                }
                label="None of the below"
                sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
              />
            )}
          </Field>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {oralCancerSymptoms.map((symptom) => (
                <TableRow key={symptom.key}>
                  <TableCell
                    sx={{
                      borderRight: "none !important",
                      py: 1,
                      pl: 0,
                      opacity: 1
                    }}
                  >
                    {symptom.label}
                  </TableCell>
                  <TableCell sx={{ py: 1, textAlign: 'right' }}>
                    <Field name={`symptoms.oral.${symptom.key}`}>
                      {({ field, form }) => (
                        <RadioGroup
                          row
                          sx={{ display: 'inline-flex' }}
                          value={field.value === true ? "Yes" : field.value === false ? "No" : ""}
                          onChange={(e) =>
                            form.setFieldValue(
                              `symptoms.oral.${symptom.key}`,
                              e.target.value === "Yes" ? true : e.target.value === "No" ? false : null
                            )
                          }
                        >
                          <FormControlLabel
                            value="Yes"
                            control={<Radio sx={{ '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }} />}
                            label="Yes"
                            disabled={!isEditing}
                            sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
                          />
                          <FormControlLabel
                            value="No"
                            control={<Radio sx={{ '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }} />}
                            label="No"
                            disabled={!isEditing}
                            sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
                          />
                        </RadioGroup>
                      )}
                    </Field>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Field name="symptoms.oral.other">
          {({ field, form }) => (
            <TextField
              fullWidth
              label="Other"
              multiline
              rows={2}
              value={field.value || ''}
              onChange={(e) => form.setFieldValue('symptoms.oral.other', e.target.value)}
              disabled={!isEditing}
              sx={{ mt: 2, opacity: 1 }}
            />
          )}
        </Field>
      </Box>

      {/* Breast Cancer Related Symptoms Section */}
      <Box className="subsection1" sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" className="section-title">Breast cancer related symptoms</Typography>
          <Field name="symptoms.breastNone">
            {({ field, form }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value || false}
                    onChange={(e) => {
                      form.setFieldValue('symptoms.breastNone', e.target.checked);
                      if (e.target.checked) {
                        breastCancerSymptoms.forEach((symptom) => {
                          form.setFieldValue(`symptoms.breast.${symptom.key}`, null);
                        });
                      }
                    }}
                    disabled={!isEditing}
                    sx={{ '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
                  />
                }
                label="None of the below"
                sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
              />
            )}
          </Field>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {breastCancerSymptoms.map((symptom) => (
                <TableRow key={symptom.key}>
                  <TableCell
                    sx={{
                      borderRight: "none !important",
                      py: 1,
                      pl: 0,
                      opacity: 1
                    }}
                  >
                    {symptom.label}
                  </TableCell>
                  <TableCell sx={{ py: 1, textAlign: 'right' }}>
                    <Field name={`symptoms.breast.${symptom.key}`}>
                      {({ field, form }) => (
                        <RadioGroup
                          row
                          sx={{ display: 'inline-flex' }}
                          value={field.value === true ? "Yes" : field.value === false ? "No" : ""}
                          onChange={(e) =>
                            form.setFieldValue(
                              `symptoms.breast.${symptom.key}`,
                              e.target.value === "Yes" ? true : e.target.value === "No" ? false : null
                            )
                          }
                        >
                          <FormControlLabel
                            value="Yes"
                            control={<Radio sx={{ '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }} />}
                            label="Yes"
                            disabled={!isEditing}
                            sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
                          />
                          <FormControlLabel
                            value="No"
                            control={<Radio sx={{ '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }} />}
                            label="No"
                            disabled={!isEditing}
                            sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
                          />
                        </RadioGroup>
                      )}
                    </Field>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Field name="symptoms.breast.othr">
          {({ field, form }) => (
            <TextField
              fullWidth
              label="Other"
              multiline
              rows={2}
              value={field.value || ''}
              onChange={(e) => form.setFieldValue('symptoms.breast.othr', e.target.value)}
              disabled={!isEditing}
              sx={{ mt: 2, opacity: 1 }}
            />
          )}
        </Field>
      </Box>

      {/* Cervical Cancer Related Symptoms Section */}
      <Box className="subsection1" sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" className="section-title">Cervical cancer related symptoms</Typography>
          <Field name="symptoms.cervicalNone">
            {({ field, form }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value || false}
                    onChange={(e) => {
                      form.setFieldValue('symptoms.cervicalNone', e.target.checked);
                      if (e.target.checked) {
                        cervicalCancerSymptoms.forEach((symptom) => {
                          form.setFieldValue(`symptoms.cervical.${symptom.key}`, null);
                        });
                      }
                    }}
                    disabled={!isEditing}
                    sx={{ '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
                  />
                }
                label="None of the below"
                sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
              />
            )}
          </Field>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {cervicalCancerSymptoms.map((symptom) => (
                <TableRow key={symptom.key}>
                  <TableCell
                    sx={{
                      borderRight: "none !important",
                      py: 1,
                      pl: 0,
                      opacity: 1
                    }}
                  >
                    {symptom.label}
                  </TableCell>
                  <TableCell sx={{ py: 1, textAlign: 'right' }}>
                    <Field name={`symptoms.cervical.${symptom.key}`}>
                      {({ field, form }) => (
                        <RadioGroup
                          row
                          sx={{ display: 'inline-flex' }}
                          value={field.value === true ? "Yes" : field.value === false ? "No" : ""}
                          onChange={(e) =>
                            form.setFieldValue(
                              `symptoms.cervical.${symptom.key}`,
                              e.target.value === "Yes" ? true : e.target.value === "No" ? false : null
                            )
                          }
                        >
                          <FormControlLabel
                            value="Yes"
                            control={<Radio sx={{ '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }} />}
                            label="Yes"
                            disabled={!isEditing}
                            sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
                          />
                          <FormControlLabel
                            value="No"
                            control={<Radio sx={{ '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }} />}
                            label="No"
                            disabled={!isEditing}
                            sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' } }}
                          />
                        </RadioGroup>
                      )}
                    </Field>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Field name="symptoms.cervical.othr">
          {({ field, form }) => (
            <TextField
              fullWidth
              label="Other"
              multiline
              rows={2}
              value={field.value || ''}
              onChange={(e) => form.setFieldValue('symptoms.cervical.othr', e.target.value)}
              disabled={!isEditing}
              sx={{ mt: 2, opacity: 1 }}
            />
          )}
        </Field>
      </Box>
    </Box>
  );
};

export default SignsSymptomsForm;
